import { Link } from "react-router-dom";

import { actorEmail, auditStamp, auditTarget } from "../../api/mcp";
import type { AnyAuditEvent, AuditProduct } from "../../hooks/useAuditFeed";
import { Badge, Table, Td, Th } from "../ui";
import { Reason } from "../write/ReasonText";
import { AuditChangeDetail } from "./AuditChangeDetail";
import { targetLink } from "./targetLink";

/**
 * One audit feed rendered the same way on Governance and on a merchant page.
 *
 * Rows are already normalised (`AnyAuditEvent`) whether they came from the
 * fleet list or `GET /admin/tenant/{id}/history`.
 */
export function AuditEventTable({
  rows,
  product,
  showTarget = true,
  showWhy = true,
}: {
  rows: AnyAuditEvent[];
  product: AuditProduct;
  showTarget?: boolean;
  showWhy?: boolean;
}) {
  return (
    <Table
      head={
        <tr>
          <Th>When</Th>
          <Th>Who</Th>
          <Th>Action</Th>
          {showTarget && <Th>Target</Th>}
          <Th>Change</Th>
          {showWhy && <Th>Why</Th>}
        </tr>
      }
    >
      {rows.map((e, i) => {
        const to = showTarget ? targetLink(e, product) : null;
        const target = auditTarget(e.target);
        return (
          <tr key={`${e.timestamp ?? "none"}-${e.action}-${i}`}>
            <Td>
              <span
                className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400"
                title={e.timestamp ?? undefined}
              >
                {auditStamp(e.timestamp)}
              </span>
            </Td>
            <Td>
              <span className="text-sm">{actorEmail(e.actor)}</span>
            </Td>
            <Td>
              <Badge>{e.action}</Badge>
              {e.domain && (
                <span className="ml-1.5 text-xs text-slate-400 dark:text-slate-500">{e.domain}</span>
              )}
            </Td>
            {showTarget && (
              <Td>
                {to ? (
                  <Link to={to} className="ref">
                    {target}
                  </Link>
                ) : target ? (
                  <span className="ref">{target}</span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500">not recorded</span>
                )}
              </Td>
            )}
            <Td>
              <div className="min-w-[12rem] max-w-md">
                <AuditChangeDetail compact before={e.before} after={e.after} action={e.action} />
              </div>
            </Td>
            {showWhy && (
              <Td>
                {e.reason ? (
                  <span className="text-xs">
                    <Reason text={e.reason} />
                  </span>
                ) : (
                  <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                )}
              </Td>
            )}
          </tr>
        );
      })}
    </Table>
  );
}
